import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import moment from "moment";
import { Icon } from "@iconify/react";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import { Dialog, IconButton, Tooltip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import "./BroadcastListDialog.css";
import BroadcastDetail from "./BroadcastDetail";
import "../../Components/Components.css";
import WarningDialog from "../../Components/WarningDialog";
import BroadcastContact from "../../Contact/BroadcastContact";

import {
  getBroadcastList,
  updateBroadcast,
} from "../../Store/Actions/WABAAction";

import { SET_BROADCAST_LIST_TABLE_PAGE } from "../../Store/Types/AgentTypes";

const BroadcastListDialog = ({ isOpen, setIsBroadcastListDialogShown }) => {
  const [broadcastList, setBroadcastList] = useState([]);
  const [isBroadcastContactShown, setIsBroadcastContactShown] = useState(false);
  const [broadcastToCancel, setBroadcastToCancel] = useState({});
  const [count, setCount] = useState(0);
  const [isCancelling, setIsCancelling] = useState(false);

  const [isCancelBroadcastWarningShown, setIsCancelBroadcastWarningShown] =
    useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [selectedBroadcast, setSelectedBroadcast] = useState(null);
  const [skip, setSkip] = useState(0);
  const dispatch = useDispatch();
  const { broadcastListTablePage } = useSelector((state) => state.agent);

  const columns = [
    {
      field: "WABAAccount",
      flex: 1,
      headerName: "WABA Account",
    },

    {
      field: "BroadcastName",
      flex: 1,
      headerName: "Broadcast Name",
    },

    {
      field: "TableName",
      flex: 1,
      headerName: "Table Name",
    },

    {
      field: "CreatedOn",
      flex: 1,
      headerName: "Created On",

      renderCell: (params) =>
        moment.utc(params.row.CreatedOn).format("DD-MMM-yyyy HH:mm:ss"),
    },

    {
      field: "RecipientFrom",
      flex: 0.5,
      headerName: "Recipient From",
    },

    {
      field: "InitiatedBy",
      flex: 1,
      headerName: "Initiated By",
    },

    {
      field: "Status",
      flex: 0.5,
      headerName: "Status",

      renderCell: (params) => (
        <p
          style={{
            color:
              params.row.Status === "NEW"
                ? "#0343C7"
                : params.row.Status === "PROCESSING"
                ? "#FF0000"
                : "#07BC0C",
          }}
        >
          {params.row.Status}
        </p>
      ),
    },

    {
      field: "BroadcastTime",
      flex: 1,
      headerName: "Broadcast Time",

      renderCell: (params) =>
        moment.utc(params.row.BroadcastTime).format("DD-MMM-yyyy HH:mm"),
    },

    {
      field: "Recipient",
      flex: 0.4,
      headerName: "Recipient",
    },

    {
      field: "Sent",
      flex: 0.4,
      headerName: "Sent",
    },

    {
      field: "Failed",
      flex: 0.4,
      headerName: "Failed",
    },

    {
      flex: 0.75,
      headerName: "Action",

      renderCell: (params) => {
        return (
          <div className="action-buttons">
            <Tooltip arrow placement="top" title="View details">
              <IconButton onClick={() => setSelectedBroadcast(params.row)}>
                <Icon
                  color="#7e7e7e"
                  height="26"
                  icon="ic:baseline-remove-red-eye"
                />
              </IconButton>
            </Tooltip>

            <Tooltip arrow placement="top" title="Cancel broadcast">
              <IconButton
                disabled={params.row.Status !== "NEW"}
                onClick={() => {
                  setBroadcastToCancel(params.row);
                  setIsCancelBroadcastWarningShown(true);
                }}
              >
                <Icon
                  color={params.row.Status === "NEW" ? "#7e7e7e" : "#cfcfcf"}
                  height="26"
                  icon="f7:clear"
                />
              </IconButton>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    (isOpen || selectedBroadcast === null) &&
      fetchBroadcastList(skip, searchValue);
  }, [isOpen, selectedBroadcast, isBroadcastContactShown]);

  function cancelBroadcast() {
    setIsCancelling(true);

    updateBroadcast({
      broadcastid: broadcastToCancel.BroadcastID,
      status: "CANCEL",
    })
      .then((response) => {
        if (response.status) {
          toast.info(
            <>
              <p>Broadcast {broadcastToCancel.name} has been cancelled.</p>
            </>,

            {
              autoClose: 5000,
            }
          );

          setIsCancelBroadcastWarningShown(false);
          setBroadcastToCancel({});
          setIsCancelling(false);
          fetchBroadcastList(skip, searchValue);
        } else {
          console.log("error cancelling broadcast", response.error);
          setIsCancelling(false);

          toast.error("error cancelling broadcast", {
            autoClose: 5000,
          });
        }
      })
      .catch((err) => {
        console.log("error cancelling broadcast", err);
        setIsCancelling(false);

        toast.error("error cancelling broadcast", {
          autoClose: 5000,
        });
      });
  }

  function fetchBroadcastList(skip, search) {
    setIsLoading(true);

    getBroadcastList(skip, search)
      .then((response) => {
        if (response.status) {
          process.env.NODE_ENV === "development" &&
            console.log("broadcast list: ", response.data.broadcastdata);

          setBroadcastList(response.data.broadcastdata ?? []);
          setCount(response.data.countdata);
          setIsLoading(false);
        } else {
          console.log("error fetching broadcast list", response.error);
          setIsLoading(false);

          toast.error("error fetching broadcast list", {
            autoClose: 5000,
          });
        }
      })
      .catch((err) => {
        console.log("error fetching broadcast list", err);
        setIsLoading(false);

        toast.error("error fetching broadcast list", {
          autoClose: 5000,
        });
      });
  }

  const handleClose = () => {
    setIsBroadcastListDialogShown(false);
    setSelectedBroadcast(null);
    setSkip(0);

    dispatch({
      type: SET_BROADCAST_LIST_TABLE_PAGE,
      payload: 0,
    });

    setSearchValue("");
  };

  return (
    <Dialog
      open={isOpen}
      className="dialog broadcast-list"
      fullWidth
      maxWidth="xl"
      scroll="paper"
      onClose={handleClose}
    >
      <div className="dialog-container">
        <div className="dialog-content">
          <IconButton className="close-button" onClick={handleClose}>
            <CloseOutlined />
          </IconButton>

          <div className="title">
            {selectedBroadcast && (
              <IconButton onClick={() => setSelectedBroadcast(null)}>
                <Icon
                  height="20"
                  icon="material-symbols:arrow-back-ios-new-rounded"
                />
              </IconButton>
            )}

            <h2>Broadcast List</h2>
          </div>

          <div className="container">
            {selectedBroadcast ? (
              <BroadcastDetail selectedBroadcast={selectedBroadcast} />
            ) : (
              <>
                <div className="contact_search">
                  <div className="search-container">
                    <SearchOutlined className="icon" />

                    <input
                      type="search"
                      className="search"
                      placeholder="Search..."
                      value={searchValue}
                      onChange={(e) => {
                        setSearchValue(e.target.value);
                      }}
                      onKeyDown={(e) =>
                        e.key === "Enter" && fetchBroadcastList(0, searchValue)
                      }
                      onInput={(e) => {
                        if (e.target.value.length === 0) {
                          setSearchValue("");
                        }
                      }}
                    />
                  </div>

                  <button
                    className="create-new-broadcast"
                    type="submit"
                    onClick={() => setIsBroadcastContactShown(true)}
                  >
                    <Icon height="20" icon="material-symbols:add-rounded" />
                    Create New Broadcast
                  </button>
                </div>

                <DataGrid
                  autoPageSize
                  className="table"
                  columns={columns}
                  disableColumnFilter
                  disableColumnMenu
                  disableSelectionOnClick
                  loading={isLoading}
                  page={broadcastListTablePage}
                  pageSize={10}
                  paginationMode={"server"}
                  rowCount={count}
                  rows={broadcastList}
                  rowsPerPageOptions={[]} // hide rows per page
                  getRowId={(row) => row.BroadcastID}
                  onPageChange={(newPage) => {
                    if (newPage > broadcastListTablePage) {
                      fetchBroadcastList(skip + 10, searchValue);
                      setSkip((prevState) => prevState + 10);
                    } else {
                      fetchBroadcastList(skip - 10, searchValue);
                      setSkip((prevState) => prevState - 10);
                    }

                    dispatch({
                      type: SET_BROADCAST_LIST_TABLE_PAGE,
                      payload: newPage,
                    });
                  }}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <WarningDialog
        confirmAction={cancelBroadcast}
        contents={
          <>
            <p>You are about to cancel "{broadcastToCancel?.BroadcastName}"</p>
            <p>Are you sure?</p>
          </>
        }
        isOpen={isCancelBroadcastWarningShown}
        isProcessing={isCancelling}
        setIsOpen={setIsCancelBroadcastWarningShown}
      />

      <BroadcastContact
        isOpen={isBroadcastContactShown}
        setIsBroadcastContactShown={setIsBroadcastContactShown}
      />
    </Dialog>
  );
};

export default BroadcastListDialog;
